import React, { FC, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TableFooter } from './GroupComparisonTable.styles';
import { useFilters } from '../../hooks/useFilters';
import { useReportContext } from '../../hooks/useReport';
import GoToPage from '../GoToPage/GoToPage';
import NoData from '../NoData/NoData';
import Pagination from '../Pagination/Pagination';
import RowsPerPage from '../RowsPerPage/RowsPerPage';
import GroupComparisonTableHeader from './GroupComparisonTableHeader/GroupComparisonTableHeader';
import GroupComparisonTableTile from './GroupComparisonTableTile/GroupComparisonTableTile';

const GroupComparisonTable: FC = () => {
  const { applyFilters } = useFilters();
  const { reportId } = useParams();
  const { t } = useTranslation();

  const { data, meta, getReportGroupComparisonDetails } = useReportContext();

  const { groupComparison } = data ?? {};
  const { groupComparison: metaGroupComparison } = meta ?? {};
  const { itemsPerPage, pageNo, totalPageCount } = metaGroupComparison ?? {};

  const isKPIAvailable = groupComparison
    ? groupComparison.some(({ kpi }) => !_.isUndefined(kpi))
    : false;
  const isRiskAvailable = groupComparison
    ? groupComparison.some(({ risk }) => !_.isUndefined(risk))
    : false;

  const returnHeaders = useMemo(() => {
    const categories =
      groupComparison && groupComparison[0]?.workEnvironment.map(({ category }) => category);

    return (
      <GroupComparisonTableHeader
        categories={categories || []}
        isKpiAvailable={isKPIAvailable}
        isRiskAvailable={isRiskAvailable}
      />
    );
  }, [groupComparison, isKPIAvailable, isRiskAvailable]);

  const renderTableBody = useMemo(() => {
    return groupComparison?.map((group, index) => (
      <GroupComparisonTableTile
        key={index}
        item={group}
        isRiskAvailable={isRiskAvailable}
        isKpiAvailable={isKPIAvailable}
      />
    ));
  }, [groupComparison, isKPIAvailable, isRiskAvailable]);

  useEffect(() => {
    (async () => {
      await getReportGroupComparisonDetails(reportId || '0');
    })();
  }, [reportId, getReportGroupComparisonDetails]);

  return (
    <>
      {!_.isEmpty(groupComparison) ? returnHeaders : null}
      {!_.isEmpty(groupComparison) ? (
        renderTableBody
      ) : (
        <NoData message={t('report.groupComparison.noData')} />
      )}
      {pageNo && itemsPerPage && totalPageCount && (
        <TableFooter id="TableFooter" data-testid="tableFooter">
          <RowsPerPage
            itemsPerPage={itemsPerPage}
            pagesCount={totalPageCount}
            menu={['10', '15', '25', '50', '75', '100']}
          />
          <Pagination
            activePage={pageNo}
            totalPageCount={totalPageCount}
            applyFilters={(pageNo: number) => applyFilters({ pageNo: String(pageNo) })}
          />
          <GoToPage applyFilters={applyFilters} pageLimit={totalPageCount} />
        </TableFooter>
      )}
    </>
  );
};

export default GroupComparisonTable;
